import React, { type FunctionComponent } from 'react'
import styled from '@emotion/styled'
import {
  Card,
  FullWidthContent,
  LoadingDots,
  Text,
  colors,
  spacing,
} from '@retailer-platform/shared-components'
import { css } from '@emotion/react'
import ko from 'legacy/common/knowledgeOwl'
import { ListAccountsGridList } from 'domains/account-management/components/accounts-list/components/list/ListAccountsGridList'
import { useAdminAccounts } from 'domains/account-management/components/accounts-list/utils/AccountManagementListAccounts.hooks'
import { ListAccountsNoResults } from 'domains/account-management/components/accounts-list/components/no-results/ListAccountsNoResults'
import { ListAccountsEmailSearchInput } from 'domains/account-management/components/accounts-list/components/search-input/ListAccountsEmailSearchInput'
import { useDomainMessage, useDomainMessages } from 'domains/account-management/utils/domain/intl'
import { AccountManagementSimpleRoleSelect } from 'domains/account-management/components/role-select/AccountManagementSimpleRoleSelect'
import { AccountManagementSimpleStatusSelect } from 'domains/account-management/components/role-select/AccountManagementSimpleStatusSelect'

const StyledLoadingDots = styled.div({
  fontSize: 0,
})

const FiltersContainer = styled.div({
  display: 'flex',
  flexDirection: 'row',
  gap: spacing.X16,
  alignItems: 'center',
})

const LinkStyles = css({
  display: 'inline-block',
  color: colors.SUCCESS.REGULAR,
})

export const accountManagementListAccountsTestIds = {
  loading: 'account-management-list-loading',
  searchInput: 'account-management-list-search-input',
  noResults: 'account-management-list-no-results',
}

interface Props {
  partnerId?: string
  partnerName?: string
}

export const AccountManagementListAccounts: FunctionComponent<Props> = ({
  partnerId,
  partnerName,
}) => {
  const {
    accounts,
    totalPages,
    loading,
    error,
    refetch,
    setRole,
    role,
    email,
    setEmail,
    status,
    setStatus,
  } = useAdminAccounts({
    partnerId: partnerId,
  })

  const allPartnersMessage = useDomainMessage('accountManagementDomain.list.allPartners')

  const knowledgeOwlMessage = useDomainMessage('accountManagementDomain.list.knowledgeOwnLink')
  const knowledgeOwlLink = (
    <Text styles={LinkStyles} variant="link" onClick={ko.showKnowledgeOwl}>
      {knowledgeOwlMessage}
    </Text>
  )

  const messages = useDomainMessages(
    {
      title: 'accountManagementDomain.list.title',
      subtitle: 'accountManagementDomain.list.subtitle',
    },
    {
      partnerName: partnerName || allPartnersMessage,
      knowledgeOwnLink: knowledgeOwlLink,
    }
  )

  return (
    <FullWidthContent>
      <FiltersContainer>
        <ListAccountsEmailSearchInput
          data-testid={accountManagementListAccountsTestIds.searchInput}
          email={email}
          setEmail={setEmail}
        />
        <div style={{ width: 270 }}>
          <AccountManagementSimpleRoleSelect value={role} onChange={setRole} />
        </div>

        <div style={{ width: 270 }}>
          <AccountManagementSimpleStatusSelect value={status} onChange={setStatus} />
        </div>

        {loading && (
          <StyledLoadingDots data-testid={accountManagementListAccountsTestIds.loading}>
            <LoadingDots height={5} />
          </StyledLoadingDots>
        )}
      </FiltersContainer>

      <Card css={{ margin: 0 }} title={messages.title} subtitle={messages.subtitle}>
        {/* This magic 335px is to make the table fill the rest of the page.
          The 335px is to account for the space above the table and below the table on the page. */}
        <div css={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 335px)' }}>
          {loading || accounts?.length ? (
            <ListAccountsGridList
              accounts={accounts || []}
              totalPages={totalPages}
              loading={loading}
              error={error}
              retailerScoped={!!partnerId}
              refetchAccounts={refetch}
            />
          ) : (
            <ListAccountsNoResults data-testid={accountManagementListAccountsTestIds.noResults} />
          )}
        </div>
      </Card>
    </FullWidthContent>
  )
}
